import { MAX_FRAME_COUNT } from "#puf/constants.ts";
import { store } from "#puf/store/index.ts";
import { closestCenter, DndContext, type DragEndEvent, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { horizontalListSortingStrategy, SortableContext, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useSelector } from "@xstate/store-react";

interface TimelineProps {
  playing: boolean;
  togglePlaying: () => void;
}

export function Timeline({ playing, togglePlaying }: TimelineProps) {
  const frames = useSelector(store, (s) => s.context.frames);
  const frameIds = useSelector(store, (s) => s.context.frameIds);
  const thumbs = useSelector(store, (s) => s.context.thumbs);
  const activeFrame = useSelector(store, (s) => s.context.activeFrame);
  const fps = useSelector(store, (s) => s.context.fps);

  const frameCount = frames.length;
  const canAdd = frameCount < MAX_FRAME_COUNT;
  const canRemove = frameCount > 1;

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
  );

  const onDragEnd = (e: DragEndEvent) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const from = frameIds.indexOf(String(active.id));
    const to = frameIds.indexOf(String(over.id));
    if (from < 0 || to < 0) return;
    store.trigger["frame.reorder"]({ from, to });
  };

  return (
    <section className="border-t border-line bg-bg-2 px-3 py-2.5 overflow-hidden min-w-0 lg:col-start-2 lg:row-start-2">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 mb-2 text-xs lg:flex-nowrap lg:gap-3.5">
        <strong>Timeline</strong>
        <span className="text-dim">
          {frameCount} {frameCount === 1 ? "frame" : "frames"}
        </span>
        <span className="text-dim">Frame {activeFrame + 1}</span>
        <span className="flex-1" />
        <label htmlFor="fps" className="text-dim">FPS</label>
        <input
          id="fps"
          name="fps"
          type="number"
          min={1}
          max={60}
          value={fps}
          onChange={(e) => {
            store.trigger["fps.set"]({ fps: Math.max(1, Math.min(60, Number(e.target.value) || 1)) });
          }}
          className="w-12 bg-bg-3 border border-line text-text px-1.5 py-0.5 rounded-[3px]"
        />
        <button
          onClick={() => {
            store.trigger["activeFrame.set"]({ index: (p) => Math.max(0, p - 1) });
          }}
          className="bg-bg-3 border border-line text-text px-2.5 py-1 rounded-[3px] cursor-pointer"
        >
          Prev
        </button>
        <button
          onClick={togglePlaying}
          className="bg-accent text-bg border border-accent font-semibold px-2.5 py-1 rounded-[3px] cursor-pointer"
        >
          {playing ? "Pause" : "Play"}
        </button>
        <button
          onClick={() => {
            store.trigger["activeFrame.set"]({ index: (p) => Math.min(frameCount - 1, p + 1) });
          }}
          className="bg-bg-3 border border-line text-text px-2.5 py-1 rounded-[3px] cursor-pointer"
        >
          Next
        </button>
      </div>
      <div className="flex gap-1.5 overflow-x-auto pb-1">
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <SortableContext items={frameIds} strategy={horizontalListSortingStrategy}>
            {frameIds.map((id, i) => (
              <FrameThumb
                key={id}
                id={id}
                index={i}
                url={thumbs[i] ?? ""}
                active={i === activeFrame}
                canRemove={canRemove}
                onActivate={() => {
                  store.trigger["activeFrame.set"]({ index: i });
                }}
                onRemove={() => {
                  store.trigger["frame.remove"]({ index: i });
                }}
              />
            ))}
          </SortableContext>
        </DndContext>
        <button
          type="button"
          onClick={() => {
            if (!canAdd) return;
            store.trigger["frame.add"]();
            store.trigger["activeFrame.set"]({ index: frameCount });
          }}
          disabled={!canAdd}
          aria-label="add frame"
          title={canAdd ? "Add frame" : `Max ${MAX_FRAME_COUNT} frames`}
          className="min-w-22.5 bg-bg-3 border-2 border-dashed border-line rounded-sm p-1 cursor-pointer text-[10px] text-dim hover:border-accent hover:text-accent disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:border-line disabled:hover:text-dim flex flex-col items-center justify-center"
        >
          <div className="w-full aspect-4/5 mb-1 flex items-center justify-center text-2xl leading-none">
            +
          </div>
          Add frame
        </button>
      </div>
    </section>
  );
}

interface FrameThumbProps {
  id: string;
  index: number;
  url: string;
  active: boolean;
  canRemove: boolean;
  onActivate: () => void;
  onRemove: () => void;
}

function FrameThumb({ id, index, url, active, canRemove, onActivate, onRemove }: FrameThumbProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      role="button"
      tabIndex={0}
      aria-pressed={active}
      aria-label={`frame ${index + 1}`}
      onClick={onActivate}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onActivate();
        }
      }}
      className={`relative min-w-22.5 bg-bg-3 border-2 rounded-sm p-1 cursor-grab active:cursor-grabbing text-[10px] focus-visible:outline focus-visible:outline-accent touch-none ${
        active ? "border-accent" : "border-line"
      }`}
    >
      {canRemove && (
        <button
          type="button"
          onPointerDown={(e) => {
            e.stopPropagation();
          }}
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          aria-label={`delete frame ${index + 1}`}
          title="Delete frame"
          className="absolute top-1 right-1 z-10 bg-bg-2/80 border border-line text-dim hover:text-danger hover:border-danger w-4 h-4 rounded-[3px] cursor-pointer text-[10px] leading-none"
        >
          ✕
        </button>
      )}
      <div
        className="w-full aspect-4/5 mb-1 bg-[#14161a] [image-rendering:pixelated] bg-no-repeat bg-center bg-contain"
        style={url ? { backgroundImage: `url(${url})` } : undefined}
      />
      Frame {index + 1}
    </div>
  );
}
